import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";

import {
    generateArtifact,
    type IArtifactGenerateRequest,
} from "@/api/artifacts";
import { downloadBlob, XLSX_MIME } from "@/utils/download-blob";

type ArtifactMutationResult = { blob: Blob; filename: string };

/**
 * Decode a `{ detail: "..." }` error body that axios has wrapped as a
 * Blob (the cost of `responseType: "blob"` at the client call site).
 * Returns `undefined` when the body isn't a parseable JSON object with
 * a string `detail` field.
 */
async function readErrorDetail(error: unknown): Promise<string | undefined> {
    if (!axios.isAxiosError(error)) return undefined;
    const data = error.response?.data;
    if (!(data instanceof Blob)) return undefined;
    try {
        const text = await data.text();
        const parsed = JSON.parse(text);
        return typeof parsed?.detail === "string" ? parsed.detail : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Compose the error toast. Always prefixed with "Couldn't download
 * Excel" so the existing integration assertions (which match on the
 * lowercased prefix) stay green, then augmented with 429/detail
 * context when we have it.
 */
function errorMessage(
    status: number | undefined,
    detail: string | undefined
): string {
    if (status === 429) {
        return "Couldn't download Excel. Too many requests; try again in a minute.";
    }
    if (detail) {
        return `Couldn't download Excel. ${detail}`;
    }
    return "Couldn't download Excel. Try again.";
}

/**
 * Mutation that POSTs an artifact-generate request, triggers a browser
 * download of the returned xlsx blob, and surfaces success/error via
 * sonner toasts. No React Query cache invalidation; there is no cached
 * list of artifacts to refresh.
 */
export function useGenerateArtifactMutation(): UseMutationResult<
    ArtifactMutationResult,
    Error,
    IArtifactGenerateRequest
> {
    return useMutation<ArtifactMutationResult, Error, IArtifactGenerateRequest>(
        {
            mutationFn: generateArtifact,
            onSuccess: ({ blob, filename }) => {
                downloadBlob(blob, filename, XLSX_MIME);
                toast.success("Excel downloaded.");
            },
            onError: async (error) => {
                const status = axios.isAxiosError(error)
                    ? error.response?.status
                    : undefined;
                const detail = await readErrorDetail(error);
                toast.error(errorMessage(status, detail));
            },
        }
    );
}
